import React, { useState, useEffect, useContext } from 'react';
import throttle from '../utils/throttle';
import { getProduct, getOffers, API_ERRORS } from '../api';
import { logError } from '../utils/logging';
import {
  getAddedOffers,
  getJustAddedOffers,
  addEligibleOffersToCart,
} from './products';
import cartWatcher from '../dom/cart-watcher';
import { isVisible } from '../dom/buttons';
import { isCartPage } from '../dom/cart';
import CartContext from './CartContext';
import { resetModifiedOffers } from './products';
import PreviewContext from './PreviewContext';
import { SETTINGS_KEY } from './products';

const FIRST_VISIT_KEY = 'giftbox-first-visit';
const debouncedAddEligibleOffersToCart = throttle(
  addEligibleOffersToCart,
  1000,
);

function shouldOpen(settings, justAddedOffers) {
  const { open_when_in_cart, open_after_receiving_gift } = settings;
  if (open_after_receiving_gift && justAddedOffers.length > 0) {
    return true;
  }
  if (open_when_in_cart && isCartPage() && !shouldOpen.did_open) {
    shouldOpen.did_open = true;
    return true;
  }

  return false;
}
shouldOpen.did_open = false;

function openOnFirstVisit(settings, setOpen, tracker, offers, cart) {
  if (
    settings.open_on_first_visit &&
    !sessionStorage.getItem(FIRST_VISIT_KEY)
  ) {
    setTimeout(() => {
      sessionStorage.setItem(FIRST_VISIT_KEY, true);
      setOpen(true);
      tracker.trackLauncherClickOpen(offers, cart);
    }, settings.open_on_first_visit_delay * 1000);
  }
}

const makeOfferMutationCallback = (args) => {
  addEligibleOffersToCart(args);
  return (mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        for (const node of mutation.removedNodes) {
          if (
            node.action &&
            (node.action.indexOf('/cart') > -1 ||
              node.action.indexOf('/checkout') > -1 ||
              node.action.indexOf('/a/checkout') > -1)
          ) {
            debouncedAddEligibleOffersToCart(args);
          }
        }
        for (const node of mutation.addedNodes) {
          if (
            node.querySelector &&
            node.querySelector(
              'form[action*="/cart"],form[action*="/checkout"],[type=submit]',
            )
          ) {
            debouncedAddEligibleOffersToCart(args);
          }

          const checkoutButton = document.querySelector(
            '[href$="checkout"]:not(.giftbox-checkout)',
          );
          if (checkoutButton && isVisible(checkoutButton)) {
            debouncedAddEligibleOffersToCart(args);
          }
        }
      } else if (mutation.type === 'attributes') {
        debouncedAddEligibleOffersToCart(args);
      }
    }
  };
};

export function useOffers(discountCode, setOpen, tracker) {
  const [data, setData] = useState({});
  const [updateCartCallback, setUpdateCartCallback] = useState(null);
  const { setCart, setCollectionsInCart } = useContext(CartContext);
  const { preloadProductsAndFilterOffers } = useContext(ProductsContext);
  const { onPreviewException, error: previewError } = useContext(
    PreviewContext,
  );

  useEffect(() => {
    async function fetchAndDoStuffWithData() {
      const data = await getOffers(window.Shopify.shop);

      if (data.error && data.error === API_ERRORS.PREVIEW_FORBIDDEN) {
        onPreviewException();
      }

      const { offers, products } = await preloadProductsAndFilterOffers(
        data.offers,
      );

      async function offersCallback(cart, collectionsInCart) {
        const addedOffers = getAddedOffers();
        setCart(cart);
        setCollectionsInCart(collectionsInCart);

        const justAddedOffers = getJustAddedOffers();

        for (const offer of offers) {
          if (justAddedOffers.find((oid) => oid === offer.id)) {
            offer.justAdded = true;
          }
          const addedOffer = addedOffers.find(
            (addedOffer) => addedOffer.offerId === offer.id,
          );
          if (addedOffer) {
            offer.added = true;
            offer.sortingThreshold = addedOffer.threshold;
          }
        }
        setData({
          offers,
          settings: data.settings,
          remainingOffers: offers.length - addedOffers.length,
        });

        if (shouldOpen(data.settings, justAddedOffers)) {
          setOpen(true);
          tracker.trackLauncherClickOpen(offers, cart);
        }
        openOnFirstVisit(data.settings, setOpen, tracker, offers, cart);
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(data.settings));
      }

      cartWatcher.rootCallback.inject(
        makeOfferMutationCallback({
          offers,
          settings: data.settings,
          products,
          offersCallback,
          discountCode,
        }),
      );

      cartWatcher.drawerCallback.inject(() => {
        return debouncedAddEligibleOffersToCart({
          offers,
          settings: data.settings,
          products,
          offersCallback,
          discountCode,
        });
      });

      cartWatcher.updateObserversFromSettings(data.settings);

      setUpdateCartCallback(() => async (discountCode) =>
        addEligibleOffersToCart({
          offers,
          settings: data.settings,
          products,
          offersCallback,
          discountCode,
        }),
      );
    }

    fetchAndDoStuffWithData();
  }, [previewError, discountCode]); // eslint-disable-line react-hooks/exhaustive-deps

  return [data, updateCartCallback];
}

const ProductsContext = React.createContext();

export function ProductsContextProvider({ children }) {
  const [products, setProducts] = useState({});

  function setProduct(handle, data) {
    setProducts((prevState) => ({
      ...prevState,
      [handle]: data,
    }));
  }

  async function loadProduct({ shopify_product_handle: handle }) {
    if (products[handle]) {
      return products[handle];
    }

    setProduct(handle, {
      loading: true,
    });

    try {
      const product = await getProduct(handle);
      product.loaded = true;
      product.loading = false;

      setProduct(handle, product);
      return product;
    } catch (error) {
      logError('Failed to load product, setting as not available.', error);
      console.warn(`GiftBox: Product ${handle} is not published.`);
      setProduct(handle, {
        loaded: true,
        loading: false,
        available: false,
      });
    }
  }

  function productOfferValid(offer, products) {
    if (offer.type === 'ProductOffer') {
      const product = products.find(
        (product) =>
          product &&
          offer.shopify_product_handle === product.handle &&
          product.available,
      );

      if (
        product &&
        (offer.shopify_product_variants.length === 0 ||
          product.variants.some(
            (v) => v.available && offer.shopify_product_variants.includes(v.id),
          ))
      ) {
        return product;
      }
    }
  }

  function multipleProductOfferValid(offer, products) {
    return (
      offer.type === 'MultipleProductOffer' &&
      offer.products.filter((offerProduct) =>
        products.find(
          (product) =>
            product &&
            offerProduct.shopify_product_handle === product.handle &&
            product.available,
        ),
      ).length >= 2
    );
  }

  function removeOffersWithUnavailableProducts(offers, products) {
    return offers.filter(
      (offer) =>
        ['FreeShippingOffer', 'ServiceOffer'].includes(offer.type) ||
        productOfferValid(offer, products) ||
        multipleProductOfferValid(offer, products),
    );
  }

  async function preloadProductsAndFilterOffers(offers) {
    const products = await Promise.all([
      ...offers
        .filter(
          (offer) =>
            offer.type === 'ProductOffer' && offer.shopify_product_handle,
        )
        .map((offer) => loadProduct(offer)),
      ...offers
        .filter((offer) => offer.type === 'MultipleProductOffer')
        .flatMap((offer) =>
          offer.products.map((offerProduct) => loadProduct(offerProduct)),
        ),
    ]);

    resetModifiedOffers(offers, products);

    return {
      offers: removeOffersWithUnavailableProducts(offers, products),
      products,
    };
  }

  return (
    <ProductsContext.Provider
      value={{
        loadProduct: loadProduct,
        getProduct: (handle) => products[handle],
        preloadProductsAndFilterOffers,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
}

export default ProductsContext;
